import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { IDatosPersonales, IPermiso, IUsuario } from 'modelos/src';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { UsuariosService } from '../../../../../auxiliares/servicios/http/usuarios.service';
import { PermisosService } from '../../../../../auxiliares/servicios/http/permisos.service';
import { LoginService } from '../../../../login/login.service';

// Configuración del propio usuario (entidad Usuario). Siempre opera sobre el
// usuario logueado vía las rutas /usuarios/propio (no requiere permiso ni rol
// de administración). Solo edita datos personales.
@Component({
  selector: 'app-configurar-usuario',
  templateUrl: './configurar-usuario.component.html',
  styleUrl: './configurar-usuario.component.scss',
  standalone: false,
})
export class ConfigurarUsuarioComponent implements OnInit {
  private service = inject(UsuariosService);
  private permisosService = inject(PermisosService);
  helper = inject(HelperService);

  public loading = false;
  public form?: FormGroup;
  public usuario?: IUsuario;
  public urlFoto?: string;
  public hoy: Date = new Date();
  public sexos = [
    { label: 'Masculino', value: true },
    { label: 'Femenino', value: false },
  ];

  // Credenciales de seguridad del usuario de monitoreo: viven en el permiso activo.
  public permisoActual?: IPermiso = LoginService.getPermisoActual();
  public credInputType: 'password' | 'text' = 'password';

  // Se editan credenciales cuando el permiso activo es de monitoreo, o cuando el
  // cliente es nivel 0 (admin) que siempre puede ver/configurar seguridad.
  get mostrarCredenciales(): boolean {
    if (LoginService.getCliente()?.nivel === 0) return true;
    return (
      !!this.permisoActual?.modulos?.moduloAlertasSeguridad &&
      !!this.permisoActual?.cliente?.config?.moduloAlertasSeguridad?.activo
    );
  }

  get esNivel0(): boolean {
    return LoginService.getCliente()?.nivel === 0;
  }

  get tieneCredenciales(): boolean {
    return (
      !!this.permisoActual?.tieneCredencialesSeguridad ||
      !!this.permisoActual?.credencialesSeguridad?.usuario
    );
  }

  get datosPersonales() {
    return this.form?.get('datosPersonales') as FormGroup;
  }

  get credenciales() {
    return this.form?.get('credenciales') as FormGroup;
  }

  public toggleCredPassword(): void {
    this.credInputType = this.credInputType === 'password' ? 'text' : 'password';
  }

  private createForm() {
    const dp = this.usuario?.datosPersonales || {};
    this.form = new FormGroup({
      datosPersonales: new FormGroup({
        nombre: new FormControl(dp.nombre),
        dni: new FormControl(dp.dni),
        sexo: new FormControl(dp.sexo),
        email: new FormControl(dp.email),
        direccion: new FormControl(dp.direccion),
        telefono: new FormControl(dp.telefono),
        fechaNacimiento: new FormControl(dp.fechaNacimiento),
        foto: new FormControl(dp.foto),
      }),
      // Sin validadores: las credenciales son opcionales. Solo se persisten si se
      // cargan usuario Y clave juntos (la clave no se puede recuperar del backend).
      credenciales: new FormGroup({
        usuario: new FormControl(
          this.permisoActual?.credencialesSeguridad?.usuario || '',
        ),
        clave: new FormControl(''),
      }),
    });
  }

  public onFotoChange(url: string) {
    this.urlFoto = url;
    this.datosPersonales?.patchValue({ foto: url });
  }

  public volver() {
    HelperService.volver();
  }

  public async onSubmit() {
    if (!this.form) return;
    this.loading = true;
    try {
      // 1) Entidad Usuario: datos personales.
      const datos: IDatosPersonales = {
        ...this.datosPersonales.getRawValue(),
        foto: this.urlFoto,
      };
      const actualizado = await this.service.updateDatosPersonalesPropio(datos);
      // Sincronizar el usuario en sesión para que el resto de la app vea los cambios.
      LoginService.setUsuario(actualizado);
      this.usuario = actualizado;

      // 2) Entidad Permiso: credenciales de seguridad (solo monitoreo).
      await this.guardarCredenciales();

      this.helper.notifSuccess('Datos actualizados correctamente');
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.loading = false;
  }

  /**
   * Persiste las credenciales de seguridad en el permiso activo. Solo actúa si el
   * permiso es de monitoreo y se cargaron usuario Y clave (la clave vacía indica
   * que no se quiere cambiar, por lo que no se pisa lo existente).
   */
  private async guardarCredenciales(): Promise<void> {
    if (!this.mostrarCredenciales || !this.permisoActual?._id) return;
    const usuario = (this.credenciales?.value?.usuario || '').trim();
    const clave = this.credenciales?.value?.clave || '';
    if (!usuario || !clave) return;

    const permisoActualizado =
      await this.permisosService.updateCredencialesSeguridad(
        this.permisoActual._id,
        {
          idCliente: this.permisoActual.idCliente,
          usuario,
          clave,
        },
      );

    // Refrescar estado local + sesión para que el badge y el resto de la app lo vean.
    this.permisoActual = permisoActualizado;
    LoginService.setPermisoActual(permisoActualizado);
    this.credenciales.patchValue({
      usuario: permisoActualizado?.credencialesSeguridad?.usuario || usuario,
      clave: '',
    });
  }

  async ngOnInit() {
    this.loading = true;
    try {
      this.usuario = await this.service.getPropio();
      this.urlFoto = this.usuario?.datosPersonales?.foto;
      this.createForm();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
    this.loading = false;
  }
}
